import GlobalClickListener from './GlobalClickListener.js';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ConfettiRain from './ConfettiRain';
import GameStats from './GameStats.js';
import { playCollectSound, playWinSound, unlockAudio } from './sound.js';
import { notifyGameEvent } from './bridge.js';
import { FREE_UNDOS } from './Entitlements.js';
import { difficultyOf } from './difficulty.js';
import { StreakEarned } from './StreakCard.js';
import Tutorial, {
  TUTORIAL_EVENT, shouldTeach, markTaught, pickTeachingMove, createTutorialGame, coversMove,
} from './Tutorial.js';
import { HardCachedGenerator, ExtremeCachedGenerator, EasyCachedGenerator, MediumCachedGenerator } from './GameGenerator.js';

/** Board coordinates of cells, read back off their ids */
const indicesOf = (cells) => cells.map((cell) => {
  const parts = cell.id.split("-");
  return { i: parseInt(parts[1]), j: parseInt(parts[2]) };
});

/** Long enough for success-pop in index.css to finish, and no longer */
const EXIT_MS = 200;

const generators = {
  Easy: new EasyCachedGenerator(),
  Medium: new MediumCachedGenerator(),
  Hard: new HardCachedGenerator(),
  Extreme: new ExtremeCachedGenerator(),
};

const TrophyIcon = ({ size = 30 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" className="trophy">
    <defs>
      <linearGradient id="trophyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffe566"/>
        <stop offset="50%" stopColor="#ffd700"/>
        <stop offset="100%" stopColor="#c9a227"/>
      </linearGradient>
      <linearGradient id="shineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#fff" stopOpacity="0"/>
        <stop offset="50%" stopColor="#fff" stopOpacity="0.6"/>
        <stop offset="100%" stopColor="#fff" stopOpacity="0"/>
      </linearGradient>
      <clipPath id="trophyClip">
        <path d="M19 5h2v4c0 1.7-1.3 3-3 3h-.3c-.9 1.8-2.5 3.1-4.7 3.5V18h3v4H8v-4h3v-2.5c-2.2-.4-3.8-1.7-4.7-3.5H6c-1.7 0-3-1.3-3-3V5h2V3h14v2z"/>
      </clipPath>
    </defs>
    <path d="M19 5h2v4c0 1.7-1.3 3-3 3h-.3c-.9 1.8-2.5 3.1-4.7 3.5V18h3v4H8v-4h3v-2.5c-2.2-.4-3.8-1.7-4.7-3.5H6c-1.7 0-3-1.3-3-3V5h2V3h14v2zM5 7v2c0 .6.4 1 1 1V7H5zm14 0v3c.6 0 1-.4 1-1V7h-1z" fill="#8b6914" transform="translate(0.5, 1)"/>
    <path d="M19 5h2v4c0 1.7-1.3 3-3 3h-.3c-.9 1.8-2.5 3.1-4.7 3.5V18h3v4H8v-4h3v-2.5c-2.2-.4-3.8-1.7-4.7-3.5H6c-1.7 0-3-1.3-3-3V5h2V3h14v2zM5 7v2c0 .6.4 1 1 1V7H5zm14 0v3c.6 0 1-.4 1-1V7h-1z" fill="url(#trophyGrad)"/>
    <g clipPath="url(#trophyClip)">
      <rect x="-10" y="0" width="15" height="24" fill="url(#shineGrad)" className="shine"/>
    </g>
  </svg>
);

const UndoIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
  </svg>
);

function Board({difficulty, active = true, startingGame = null, onSolved = () => {}, onShowUnlimited = null, unlimited = false, onTutorialChange = () => {} }) {
    // A new player's very first board is the lesson itself, not a generated one
    // swapped out a tick later - decided once, before anything renders.
    const openWithTutorial = useRef(shouldTeach(GameStats.getDistinctGames().length > 0));

    // Lazy: without the thunk a board is generated on every single render
    const [game, setGame] = useState(() => (openWithTutorial.current
      ? createTutorialGame()
      : generators[difficulty].generate()));
    const [initialGame, setInitialGame] = useState(game);
    // Every board state before a match, newest last. Cleared with the board.
    const [past, setPast] = useState([]);
    const [undosUsed, setUndosUsed] = useState(0);
    // null | 'playing' | 'cleared'. The tutorial runs on its own throwaway
    // board, so nothing it does counts - see tutorialRef below.
    const [tutorial, setTutorial] = useState(openWithTutorial.current ? 'playing' : null);
    // Mirrors `tutorial` for the effects that must not re-run when it changes
    const tutorialRef = useRef(false);
    tutorialRef.current = tutorial !== null;

    const numbers = game.getNumbers();
    const done = game.isDone();

    // Mirrors `done` for the mode-change effect, which must not re-run on every
    // move just to know whether the board it is replacing was finished
    const doneRef = useRef(false);
    doneRef.current = done;

    // Memoised for its identity as much as its cost: it is the dependency chain
    // Tutorial measures against, and a fresh object every render restarted that
    // measurement's timer every render, so it landed whenever it happened to.
    const teachingMove = useMemo(
      () => (tutorial === 'playing' ? pickTeachingMove(game) : null),
      [tutorial, game]
    );
    const isTaught = (row, col) => teachingMove
      && row >= teachingMove.top && row <= teachingMove.bottom
      && col >= teachingMove.left && col <= teachingMove.right;

    // Names the mistake just made on the lesson board - see onRelease
    const [nudge, setNudge] = useState(null);
    const nudgeTimer = useRef(null);
    const showNudge = (kind) => {
      setNudge(kind);
      clearTimeout(nudgeTimer.current);
      nudgeTimer.current = setTimeout(() => setNudge(null), 2600);
    };
    useEffect(() => () => clearTimeout(nudgeTimer.current), []);

    // Tiles mid-exit: "i-j" to the number that was on it. Cosmetic only - see
    // onRelease, where the board is updated before this is.
    const [clearing, setClearing] = useState(null);
    const clearingTimer = useRef(null);
    const stopClearing = () => {
      clearTimeout(clearingTimer.current);
      setClearing(null);
    };
    useEffect(() => () => clearTimeout(clearingTimer.current), []);

    // Set for the single stats pass that an undo causes - see the effect below
    const undoingRef = useRef(false);
    // The last board state reported to stats, so a re-render cannot report it
    // twice - see the same effect
    const recordedRef = useRef(null);

    const containerRef = useRef(null);
    function getCells() {
        if (!containerRef.current) return [];
        return Array.from(containerRef.current.getElementsByClassName("cell"));
    }

    const waveCells = () => {
        setTimeout(() => {
             getCells().forEach((cell) => {
            cell.classList.remove("wave");  // queued
            void cell.offsetWidth;          // forces browser to process now
            cell.classList.add("wave");     // fresh addition → animation restarts
        });
        }, 10); // defer to next tick
    }
    const waveCellsRef = useRef(waveCells)

    // One rect read per cell, and the callers below work off the result rather
    // than asking again - a selection used to be measured three or four times
    // over, which on Extreme meant thousands of forced layouts per drag.
    function getSelectedCells({p0, p1}) {
        return getCells().filter((cell) => {
            const cellRect = cell.getBoundingClientRect();
            return p0.x < cellRect.right && p1.x > cellRect.left &&
                p0.y < cellRect.bottom && p1.y > cellRect.top;
        })
    }

  const onComplete = () => {
    console.log("Game complete!");
    // The tutorial board is not a game - it must not drive ads or analytics
    if (!tutorialRef.current) notifyGameEvent("gameCompleted");
    playWinSound();
  };

  // A board's undo stack and its allowance both belong to that board alone
  const loadBoard = (nextGame) => {
    // Whatever was animating away belonged to the last board
    stopClearing();
    setGame(nextGame);
    setInitialGame(nextGame);
    setPast([]);
    setUndosUsed(0);
    waveCellsRef.current();
  };
  const loadBoardRef = useRef(loadBoard);
  loadBoardRef.current = loadBoard;

  // Restarting the same board is a fresh attempt, so the allowance comes back.
  // On the lesson board that means running the lesson again.
  const tryAgain = () => {
    stopClearing();
    setGame(initialGame);
    setPast([]);
    setUndosUsed(0);
    if (tutorialRef.current) setTutorial('playing');
    // The same board again, but the player is leaving a completion screen -
    // which is the boundary that matters, not whether the numbers changed.
    // The lesson board is not a game and must never drive an ad.
    else notifyGameEvent("gameStarted");
  };

  const newGame = (difficulty) => {
    setTutorial(null);
    loadBoard(generators[difficulty].generate());
    notifyGameEvent("gameStarted");
  }

  useEffect(() => { waveCellsRef.current(); }, []);

  // Mid-lesson, picking a mode would throw the lesson away mid-sentence. Only
  // while it is running: once cleared, changing mode is how they move on.
  useEffect(() => {
    onTutorialChange(tutorial === 'playing');
  }, [tutorial, onTutorialChange]);

  // Two jobs, because they have the same answer: load a board when the mode
  // changes, and load one when the board on screen is not the size the mode
  // says it should be.
  //
  // The second is a repair. The board being a size nobody selected has been
  // reported on device, coming back from background, on a build where nothing
  // in the play path writes board state from a timer any more - so the cause
  // is not yet known. Whatever it is, one more render of a board from a mode
  // the player did not pick is one too many, and the check is a dimension
  // compare on state we already hold.
  const loadedDifficulty = useRef(difficulty);
  // Bounded, so a repair can never become a render loop - if DIFFICULTIES and
  // the generators' getSize() ever disagree, this gives up instead of spinning
  const repairs = useRef(0);
  useEffect(() => {
    const modeChanged = loadedDifficulty.current !== difficulty;
    if (modeChanged) repairs.current = 0;

    // Neither of these is the mode's board, and neither is wrong: the lesson
    // is its own fixed grid, and a board replayed from history sets the mode
    // to match itself rather than the other way round
    const ownsTheBoard = !tutorial && game !== startingGame;
    // Only when it is recognisably *another* mode's board. A size that matches
    // no mode says nothing - it is a fixture, or a shape DIFFICULTIES does not
    // describe - and guessing about it would throw away a board on a hunch.
    const looksLike = ownsTheBoard ? difficultyOf(game.getNumbers())?.name : undefined;
    const wrongSize = looksLike !== undefined
      && looksLike !== difficulty
      && repairs.current < 2;

    if (!modeChanged && !wrongSize) return;

    if (wrongSize && !modeChanged) {
      repairs.current += 1;
      console.warn(`Board was a ${looksLike} board while the mode was ${difficulty} - regenerating`);
    }

    loadedDifficulty.current = difficulty;
    // Both refs still describe the board being left behind - they are set
    // during render, and the render that changed the mode had not swapped the
    // board out yet
    const leavingFinishedBoard = doneRef.current && !tutorialRef.current;
    // Picking a mode is asking for a real board, so it drops the tutorial
    setTutorial(null);
    loadBoardRef.current(generators[difficulty].generate());
    // Only off a completion screen. Switching mode mid-board is someone
    // deciding this puzzle is the wrong size, and an ad in the middle of that
    // thought is an interruption rather than a break.
    if (leavingFinishedBoard) notifyGameEvent("gameStarted");
  }, [difficulty, game, startingGame, tutorial]);

  // Load a board handed to us from elsewhere (replaying one from history)
  useEffect(() => {
    if (startingGame) {
      setTutorial(null);
      loadBoardRef.current(startingGame);
      notifyGameEvent("gameStarted");
    }
  }, [startingGame]);

  const startTutorial = useCallback(() => {
    loadBoardRef.current(createTutorialGame());
    setTutorial('playing');
  }, []);

  // Replayable from Help long after the first launch
  useEffect(() => {
    window.addEventListener(TUTORIAL_EVENT, startTutorial);
    return () => window.removeEventListener(TUTORIAL_EVENT, startTutorial);
  }, [startTutorial]);

  const undosLeft = Math.max(0, FREE_UNDOS - undosUsed);
  const spent = !unlimited && undosLeft === 0;

  const undo = () => {
    // The wall itself is the pitch - open the sheet rather than going dead
    if (spent) {
      if (onShowUnlimited) onShowUnlimited({ from: 'undo', undoLimit: FREE_UNDOS });
      return;
    }
    if (past.length === 0) return;
    undoingRef.current = true;
    stopClearing();
    setGame(past[past.length - 1]);
    setPast((states) => states.slice(0, -1));
    if (!unlimited) setUndosUsed((n) => n + 1);
  };

  const clearHighlights = () => {
    getCells().forEach((cell) => {
      cell.classList.remove("highlight");
    });
  }

  const onSelect = (e) => {
    clearHighlights();
    getSelectedCells(e).forEach((cell) => {
      cell.classList.add("highlight");
    })
  };

  const shake = (cells) => {
    cells.forEach((cell) => {
      cell.classList.remove("wave");
      cell.classList.add("wrong");
      setTimeout(() => {
        cell.classList.remove("wrong");
      }, 190);
    });
  };

  const onRelease = (e) => {
    unlockAudio();
    clearHighlights();

    const cells = getSelectedCells(e);
    const indices = indicesOf(cells);
    const sum = indices.reduce((total, {i, j}) => total + numbers[i][j], 0);

    // The lesson takes its moves in order. Other tens are on the board, and
    // taking one leaves the next prompt describing a shape they never dragged.
    if (teachingMove && cells.length > 0 && !coversMove(teachingMove, indices)) {
      showNudge(cells.length === 1 ? 'tap' : 'off');
      shake(cells);
      return;
    }

    if (sum === 10) {
      const taken = new Set(indices.map(({i, j}) => `${i}-${j}`));
      const newNumbers = numbers.map((row, i) =>
        row.map((num, j) => (taken.has(`${i}-${j}`) ? 0 : num))
      );
      const updatedGame = game.withNumbers(newNumbers);

      // What the tiles on their way out should keep showing while they go. The
      // board itself is already correct by the end of this handler - this only
      // decides what is drawn, so a timer that fires late, or never, can leave
      // an animation running but can no longer change what is on the board.
      const exiting = {};
      indices.forEach(({i, j}) => {
        if (numbers[i][j] !== 0) exiting[`${i}-${j}`] = numbers[i][j];
      });

      playCollectSound();
      // Applied on release, not on a timer. Every version of this that waited
      // left the component holding a board that no longer existed, and a second
      // drag inside the wait was judged against it and then wrote over the
      // first move - tiles vanishing and coming back.
      if (tutorialRef.current && updatedGame.isClear()) {
        markTaught();
        setTutorial('cleared');
      }
      setPast((states) => [...states, game]);
      setGame(updatedGame);
      setClearing(exiting);
      if (updatedGame.isDone()) {
        onComplete();
      }

      // The class and the emptied value now arrive in the same commit, so the
      // tile cannot be restored to full size a frame before the value that
      // hides it lands. That mismatch is why this used to be done by hand.
      clearTimeout(clearingTimer.current);
      clearingTimer.current = setTimeout(() => setClearing(null), EXIT_MS);
    } else {
      shake(cells);
    }
  };

  useEffect(() => {
    // Once per board state, not once per render. `onSolved` below defaults to a
    // fresh function on every render, so this effect re-runs constantly - and
    // since a cleared board stays cleared and the sum stays ten below the
    // start, every one of those passes used to file another clear or another
    // attempt, inflating history and POSTing a duplicate to analytics each
    // time. Game objects are replaced rather than mutated, so identity is
    // exactly "this is a state we have already reported".
    if (recordedRef.current === game) return;
    recordedRef.current = game;

    // Undo rewinds into states already recorded, and every record is also an
    // analytics POST. Skip only the pass the undo itself caused.
    if (undoingRef.current) {
      undoingRef.current = false;
      return;
    }
    // Recorded, but tagged - the lesson board is kept out of history and stats
    // while still letting us measure how many new players finish it
    const options = { isTutorial: tutorialRef.current };
    if (game.isClear()) {
      GameStats.recordClear(initialGame, options);
      onSolved();
    } else if (game.isDone()) {
      GameStats.recordComplete(initialGame, options);
      onSolved();
    } else if (initialGame.sum() - game.sum() === 10) {
      GameStats.recordAttempt(initialGame, options);
    }
  }, [game, initialGame, difficulty, onSolved]);

  return (
    <>
      { active && !done && <GlobalClickListener onSelect={onSelect} onRelease={onRelease} onCancel={clearHighlights} />}
      <section className="title-section">
        <h1
          style={{color: game.isClear() ? "lightgreen" : difficulty === "Extreme" ? "#CD1C18" : undefined}}
        >
          { game.isClear() ? "Cleared!": done ? "Complete" : "Total Ten" }
        </h1>
        {/* Nothing under "Cleared!" - the streak line below the trophy is the
            second line on this screen now, and it says something they did not
            already know from the heading */}
        { !game.isClear() && (
          done ? <small>Nice! Now try clearing the whole board.</small>
            : difficulty === "Extreme" && <small>The void gazes back — and it knows arithmetic</small>
        )}
      </section>
      {game.isClear() && <ConfettiRain />}

      {/* Not the instant the last tiles are taken - the board is already clear
          by then, and swapping the grid out from under them would mean the
          winning move is the one move nobody ever sees play */}
      {game.isClear() && !clearing ? <TrophyIcon size={64} color="white" />
      : <div
          className={"grid" + (tutorial === 'playing' ? " tutorial-focus" : "")}
          ref={containerRef}
          /* How big a cell may grow depends on how many have to fit - see .grid */
          style={{ "--rows": numbers.length, "--cols": numbers[0].length }}
        >
        {
          numbers.map((row, rowIndex) => (
            <div className="row" key={rowIndex}>
              {row.map((num, colIndex) => {
                // Taken, but still on its way out: it keeps the number it had
                // until the animation ends, because a tile shrinking away
                // reading "0" is not the tile the player just matched
                const leaving = clearing?.[`${rowIndex}-${colIndex}`];
                return (
                  <div
                    id={`cell-${rowIndex}-${colIndex}`}
                    className={"cell" + (done ?  " done" : "")
                      + (leaving !== undefined ? " clearing" : "")
                      + (isTaught(rowIndex, colIndex) ? " tutorial-target" : "")}
                    key={2**colIndex * 3**rowIndex}
                    style={{opacity: num === 0 && leaving === undefined ? 0 : 1, "--row": rowIndex, "--col": colIndex}}
                  >
                    {leaving !== undefined ? leaving : num}
                  </div>
                );
              })}
            </div>
          ))
        }
      </div>}

      {/* Under the trophy, where they are already looking. Not on the lesson
          board, which is kept out of history and so cannot move the count. */}
      { game.isClear() && !tutorial && <StreakEarned /> }

      { active && tutorial && (
        <Tutorial
          step={tutorial}
          move={teachingMove}
          numbers={numbers}
          nudge={nudge}
        />
      )}

      {/* Kept on a Complete board - backing out of a dead end to find the
          clearing line is undo's whole point, and it sits above the New Game
          row rather than competing in it. Meaningless once cleared. */}
      {/* Hidden during the tutorial - stepping back is not the lesson, and it
          would spend a real undo on a throwaway board */}
      { !game.isClear() && !tutorial && <div className="undo-container">
        <button
          className={'undo-btn' + (spent ? ' undo-spent' : '')}
          onClick={undo}
          disabled={past.length === 0 && !spent}
          aria-label={unlimited ? 'Undo' : `Undo, ${undosLeft} left`}
        >
          <UndoIcon />
          { !unlimited && <span className="undo-count">{undosLeft}</span> }
        </button>
      </div>}
      <br />
      <br />
      { done && <>
        <span className='button-container'>
          <button className='play-again-btn' onClick={() => newGame(difficulty)}>New Game</button>
          {' '}
          <button className='try-again-btn' onClick={tryAgain}>Try Again</button>
        </span>
        {/* Between boards is the moment to ask - not while they are mid-puzzle */}
        { !unlimited && onShowUnlimited && <button className='unlimited-inline' onClick={() => onShowUnlimited({ from: 'ads' })}>
          <span className="unlimited-cta-mark">✦</span> Remove ads with Unlimited <span className="unlimited-cta-mark">✦</span>
        </button>}
      </>}
    </>
  );
}

export default Board;
